import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ApiClient } from "./client";
import { createMockApiClient } from "./mockClient";
import { createRealApiClient } from "./realClient";

const ApiContext = createContext<ApiClient | undefined>(undefined);

/** The mock stays available behind an explicit opt-in — component tests and any
 * dev workflow that wants to run without a local control plane — but the app
 * talks to the real backend by default. */
function defaultApiClient(): ApiClient {
  return import.meta.env.VITE_USE_MOCK === "1" ? createMockApiClient() : createRealApiClient();
}

/**
 * Injects the API client for the whole app. This is the seam: swapping clients
 * (real vs. mock, or any future adapter) is a one-line change here (pass a
 * different `client` prop, or change the default factory) — nothing that calls
 * `useApiClient()` needs to change.
 */
export function ApiProvider({ client, children }: { client?: ApiClient; children: ReactNode }) {
  const value = useMemo(() => client ?? defaultApiClient(), [client]);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApiClient(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error("useApiClient must be used within an ApiProvider");
  return client;
}
